import { useState } from "react";
import { AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { TAMANHO_DA_PAGINA, useCriarPessoa, usePaginaDePessoas } from "@/api/consultas";
import { FalhaDaApi } from "@/api/problema";
import { useAvisos } from "@/componentes/Avisos";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { rotaDoCedente } from "@/rotas";
import { useSessao } from "@/sessao/useSessao";
import { mesmoDocumento, validarCriacao, VALORES_INICIAIS } from "./cedente-formulario";
import { FormularioDeCedente, useFormularioDeCedente } from "./FormularioDeCedente";

/*
 * Cadastro de cedente: uma transação, um modal (doc 06 §4 e spec "Os cadastros são
 * modais encadeados"). Conta e convênio são outras transações, e por isso criar leva à
 * página do cedente, onde a pendência oferece o próximo passo.
 *
 * Quem abre monta este componente, e quem fecha o desmonta: é isso que limpa campos e
 * recusa ao cancelar.
 */
export function NovoCedente({
  aoFechar,
  aoCriar,
}: {
  aoFechar: () => void;
  /** Recebe o id recém-criado, para a tela navegar até a página dele. */
  aoCriar: (pessoaID: string) => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const criar = useCriarPessoa(cliente);
  const formulario = useFormularioDeCedente(VALORES_INICIAIS);
  const [duplicado, setDuplicado] = useState<string | null>(null);

  function enviar() {
    const corpo = formulario.conferir(validarCriacao(formulario.valores));
    if (corpo === null) return;

    setDuplicado(null);
    criar.mutate(corpo, {
      onSuccess: ({ id }) => {
        avisos.feito("Cedente cadastrado");
        aoCriar(id);
      },
      onError: (falha) => {
        // `409`, e não `422`: o que digitou está certo, e a saída é o cadastro que já
        // existe. Por isso esta recusa não vai para o campo, e sim para o link.
        if (falha instanceof FalhaDaApi && falha.codigo === "cadastro_duplicado") {
          setDuplicado(corpo.documento);
        } else {
          formulario.recusar(falha);
        }
      },
    });
  }

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo="Novo cedente"
      aoEnviar={enviar}
      enviando={criar.isPending}
      erro={formulario.erroGeral}
    >
      <FormularioDeCedente formulario={formulario} />

      {/* Corrigiu o documento, a recusa deixou de valer para o que está na tela. */}
      {duplicado === formulario.valores.documento && <CedenteExistente documento={duplicado} />}
    </ModalDeCadastro>
  );
}

/**
 * A recusa com a saída, no lugar da recusa geral do modal, que só aceita texto.
 *
 * O `409 cadastro_duplicado` não traz o `id`: o link sai da busca pelo documento. Se a
 * busca falhar, a frase continua verdadeira, e só o link não aparece.
 */
function CedenteExistente({ documento }: { documento: string }) {
  const { cliente } = useSessao();
  const { data } = usePaginaDePessoas(cliente, { q: documento, limite: TAMANHO_DA_PAGINA }, true);
  const existente = data?.pessoas.find((pessoa) => mesmoDocumento(pessoa.documento, documento));

  return (
    <p
      role="alert"
      className="flex items-start gap-2 rounded-controle border border-falha bg-falha/10 px-3 py-2 text-corpo text-falha"
    >
      <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <span>
        Documento já cadastrado
        {existente === undefined ? (
          "."
        ) : (
          <>
            :{" "}
            <Link to={rotaDoCedente(existente.id)} className="text-texto underline underline-offset-2">
              {existente.razao_social}
            </Link>
          </>
        )}
      </span>
    </p>
  );
}
